// Les surfaces neutres utilisent un token privé pour suivre le thème, avec leur couleur claire en repli.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Spin, 
  Tag, 
  Button, 
  Row, 
  Col, 
  Descriptions, 
  Table, 
  Space, 
  message, 
  Breadcrumb,
  Image,
  Divider,
  Select,
  Form,
  Modal,
  Input,
  DatePicker,
  Dropdown,
  Menu,
  InputNumber,
  Checkbox,
  Radio,
  Grid
} from 'antd';
import ColumnsCardList from '../common/ColumnsCardList';
import {
  ArrowLeftOutlined,
  HomeOutlined,
  ShoppingOutlined,
  LoadingOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  CarOutlined,
  UserOutlined,
  LeftOutlined,
  RightOutlined,
  MoreOutlined,
  DollarOutlined,
  StopOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  LinkOutlined,
  WhatsAppOutlined
} from '@ant-design/icons';
import { ordersApi, deliveryPersonsApi, unresponsiveCustomersApi, productsApi, whatsappApi } from '../../utils/api';
// Modal générique d'envoi WhatsApp + templates de messages liés aux commandes
import SendWhatsAppMessageModal from '../common/SendWhatsAppMessageModal';
import WhatsAppProviderBadge from '../whatsapp/WhatsAppProviderBadge';
import {
  TEMPLATES_MESSAGES_COMMANDE,
  VARIABLES_COMMANDE,
  construireVariablesCommande
} from '../../constants/whatsappMessageTemplates';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
// Accent contextuel : noir dans l'espace privé, bleu en dehors.
import { PRIVATE_PRIMARY_COLOR as PRIMARY_COLOR } from '../../theme/privateTheme';
// Partage la configuration du défilement tactile des listes de livreurs.
import { getDeliveryPersonSelectProps } from '../../utils/deliveryPersonSelect';
import { getDeviseActive } from '../../utils/devise';

const { Title, Text } = Typography;

// Initialiser les plugins dayjs
dayjs.extend(isBetween);
dayjs.extend(utc);

/**
 * Libellé de la variation à afficher sous le nom d'un article.
 * Retourne null si la variation est vide, "Default (Title)" ou déjà incluse dans le nom.
 * @param {string} name - Nom de l'article (product_name)
 * @param {string} variantTitle - Titre de la variation
 */
const getVariantLabel = (name, variantTitle) => {
  if (!variantTitle || /^default( title)?$/i.test(variantTitle.trim())) return null;
  if ((name || '').toLowerCase().includes(variantTitle.toLowerCase())) return null;
  return variantTitle;
};

const OrderDetails = () => {
  // Mobile (< md) : cartes à la place du tableau des articles ; `=== false` évite un flash avant la mesure
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // États pour la modification
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [loadingDeliveryPersons, setLoadingDeliveryPersons] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // États pour l'attribution de livreur
  const [isAssignmentModalVisible, setIsAssignmentModalVisible] = useState(false);
  const [assignmentForm] = Form.useForm();
  const [deliveryPersonsStats, setDeliveryPersonsStats] = useState({});

  // États pour la navigation entre commandes
  const [ordersList, setOrdersList] = useState([]);
  const [currentOrderIndex, setCurrentOrderIndex] = useState(-1);
  const [loadingNavigation, setLoadingNavigation] = useState(false);

  // États pour le paiement
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
  const [paymentForm] = Form.useForm();

  // États pour l'annulation
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [cancelForm] = Form.useForm();

  // États pour le remboursement
  const [isRefundModalVisible, setIsRefundModalVisible] = useState(false);
  const [refundForm] = Form.useForm();

  // États pour le report de commande
  const [isRescheduleModalVisible, setIsRescheduleModalVisible] = useState(false);
  const [rescheduleForm] = Form.useForm();

  // État pour la réservation de stock
  const [reservingStock, setReservingStock] = useState(false);

  // État pour l'envoi du lien de feedback WhatsApp
  const [sendingFeedbackLink, setSendingFeedbackLink] = useState(false);

  // État pour le modal d'envoi de message WhatsApp au client (messages prédéfinis)
  const [isWhatsAppModalVisible, setIsWhatsAppModalVisible] = useState(false);

  // États pour l'édition des items de la commande
  const [isEditItemsModalVisible, setIsEditItemsModalVisible] = useState(false);
  const [editItemsForm] = Form.useForm();
  const [editableItems, setEditableItems] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchProductText, setSearchProductText] = useState('');

  // Fonction pour décoder les filtres depuis l'URL
  const getFiltersFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const returnFilters = params.get('returnFilters');
    
    if (!returnFilters) return {};
    
    try {
      const decodedParams = new URLSearchParams(decodeURIComponent(returnFilters));
      
      return {
        deliveryDate: decodedParams.get('deliveryDate') || null,
        date: decodedParams.get('date') || null,
        deliveryPersonId: decodedParams.get('deliveryPersonId') || null,
        status: decodedParams.get('status') || null,
        customerId: decodedParams.get('customerId') || null,
        tags: decodedParams.get('tags') ? decodedParams.get('tags').split(',') : [],
        searchText: decodedParams.get('searchText') || null
      };
    } catch (error) {
      console.error('Erreur lors du décodage des filtres:', error);
      return {};
    }
  };

  // Charger les détails de la commande via l'API serveur
  const loadOrderDetails = async () => {
    try {
      setLoading(true);
      
      // Utiliser l'API serveur pour récupérer les détails de la commande
      const response = await ordersApi.getById(id);
      console.log(response);
             if (response.data) {
         // L'API serveur retourne directement les données avec les order_items inclus
         setOrder(response.data);
       } else {
         throw new Error('Erreur lors du chargement de la commande');
       }
      
    } catch (error) {
      console.error('Error loading order details:', error);
      
      if (error.response?.status === 404) {
        message.error("Commande non trouvée");
      } else if (error.response?.status === 401) {
        message.error("Vous devez être connecté pour voir les détails de la commande");
      } else {
        message.error("Erreur lors du chargement des détails de la commande");
      }
      
      // En cas d'erreur, retour vers l'onglet orders
      navigate('/app/orders?tab=orders');
    } finally {
      setLoading(false);
    }
    };

  // Charger les statistiques des livreurs
  // Accepte un paramètre persons pour éviter les problèmes de timing avec les states React
  const loadDeliveryPersonsStats = async (persons = deliveryPersons) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      console.log("deliveryPersons reçus pour stats:", persons);

      // Utiliser le paramètre persons au lieu de l'état deliveryPersons
      const statsPromises = persons.map(person =>
        deliveryPersonsApi.getDailyStats(person.id, today)
      );
      console.log("statsPromises", statsPromises);
      const statsResults = await Promise.all(statsPromises);

      const statsMap = {};
      statsResults.forEach((result, index) => {
        if (result && result.data) {
          statsMap[persons[index].id] = result.data;
        } else {
          statsMap[persons[index].id] = { orders_count: 0 };
        }
      });

      setDeliveryPersonsStats(statsMap);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  // Charger la liste des livreurs
  const loadDeliveryPersons = async () => {
    try {
      setLoadingDeliveryPersons(true);
      // Utiliser l'API existante pour récupérer les livreurs actifs
      const response = await deliveryPersonsApi.getAll({ is_active: true });
      console.log("response", response);
      if (response && response.data) {
        setDeliveryPersons(response.data || []);
        // Passer directement les données à loadDeliveryPersonsStats pour éviter le problème de timing
        // avec setDeliveryPersons qui est asynchrone
        await loadDeliveryPersonsStats(response.data);
      } else {
        throw new Error('Erreur lors du chargement des livreurs');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des livreurs:', error);
      message.error('Erreur lors du chargement des livreurs');
    } finally {
      setLoadingDeliveryPersons(false);
    }
  };

  // Charger la liste des commandes pour la navigation
  const loadOrdersList = async () => {
    try {
      setLoadingNavigation(true);
      
      // Récupérer les filtres appliqués depuis l'URL
      const urlFilters = getFiltersFromUrl();
      console.log("urlFilters", urlFilters);
      // Préparer les filtres pour l'API (même logique que OrdersList)
      const filters = {
        include_items: false,
        order_by: 'created_at',
        ascending: false,
        limit: 1000 // Récupérer un grand nombre pour avoir toute la liste
      };

      // Appliquer les filtres de l'URL
      if (urlFilters.deliveryPersonId) {
        filters.delivery_person_id = urlFilters.deliveryPersonId;
      }
      if (urlFilters.deliveryDate) {
        const dateStrd = urlFilters.deliveryDate;
        const startDate = `${dateStrd} 00:00:00`;
        const endDate = `${dateStrd} 23:59:59`;
        filters.delivery_date_from = startDate;
        filters.delivery_date_to = endDate;
      }
      if (urlFilters.date) {
        const dateStr = urlFilters.date;
        const startDate = `${dateStr} 00:00:00`;
        const endDate = `${dateStr} 23:59:59`;
        filters.created_at_from = startDate;
        filters.created_at_to = endDate;
      }

      if (urlFilters.status) {
        filters.status = urlFilters.status;
      }

      if (urlFilters.customerId) {
        filters.customer_id = urlFilters.customerId;
      }

      if (urlFilters.tags && urlFilters.tags.length > 0) {
        filters.tags = urlFilters.tags.join(',');
      }
      
      console.log('Filters appliqués pour la navigation:', filters);
      
      // Récupérer les commandes avec les mêmes filtres que la liste principale
      const response = await ordersApi.getAll(filters);
      
      if (response && response.data) {
        const orders = response.data;
        setOrdersList(orders);
        
        // Trouver l'index de la commande actuelle
        const currentIndex = orders.findIndex(o => o.id === id);
        setCurrentOrderIndex(currentIndex);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la liste des commandes:', error);
    } finally {
      setLoadingNavigation(false);
    }
  };

  // Ouvrir le modal de modification
  const handleEditClick = () => {
    form.setFieldsValue({
      status: order.status,
      delivery_person_id: order.delivery_person_id,
      delivery_notes: order.delivery_notes
    });
    setIsEditModalVisible(true);
    loadDeliveryPersons();
  };

  // Sauvegarder les modifications
  const handleSave = async (values) => {
    try {
      setSaving(true);
      
      // Préparer les données à mettre à jour
      const updateData = {};
      
      // Vérifier les changements de statut
      if (values.status !== order.status) {
        updateData.status = values.status;
      }
      
      // Vérifier les changements de livreur
      if (values.delivery_person_id !== order.delivery_person_id) {
        updateData.delivery_person_id = values.delivery_person_id;
      }
      
      // Vérifier les changements de notes de livraison
      if (values.delivery_notes !== order.delivery_notes) {
        updateData.delivery_notes = values.delivery_notes;
      }
      
      // S'il n'y a pas de changements
      if (Object.keys(updateData).length === 0) {
        setIsEditModalVisible(false);
        message.info('Aucune modification détectée');
        return;
      }
      
      // Effectuer la mise à jour via l'API existante
      const response = await ordersApi.update(id, updateData);
      
      if (response && response.data) {
        message.success('Commande mise à jour avec succès');
        setIsEditModalVisible(false);
        // Recharger les détails de la commande
        await loadOrderDetails();
      } else {
        throw new Error('Erreur lors de la mise à jour');
      }
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      message.error('Erreur lors de la mise à jour de la commande');
    } finally {
      setSaving(false);
    }
  };

  // Ouvrir le modal d'attribution de livreur
  const showAssignmentModal = () => {
    // Initialiser le formulaire avec les valeurs par défaut
    // Utiliser moment.utc() pour parser la date sans conversion de fuseau horaire
    assignmentForm.setFieldsValue({
      delivery_person_id: order.delivery_person_id || null,
      delivery_date: order.delivery_date ? dayjs.utc(order.delivery_date) : null,
      shipping_fee: order.shipping_fee || 0,
      delivery_message_mode: 'scheduled'
    });
    
    setIsAssignmentModalVisible(true);
    // if (deliveryPersons.length === 0) {
      loadDeliveryPersons();
    // }
  };

  // Attribuer la commande à un livreur
  const handleAssignOrder = async () => {
    try {
      const values = await assignmentForm.validateFields();
      
      setSaving(true);
      
      // Utiliser l'API d'attribution des commandes
      const assignmentData = {
        delivery_person_id: values.delivery_person_id,
        delivery_date: values.delivery_date ? values.delivery_date.format('YYYY-MM-DD') : null,
        shipping_fee: values.shipping_fee || 0,
        delivery_message_mode: values.delivery_message_mode
      };

      await ordersApi.assignOrders([order.id], values.delivery_person_id, assignmentData);
      
      message.success('Commande attribuée avec succès');
      setIsAssignmentModalVisible(false);
      
      // Recharger les détails de la commande
      await loadOrderDetails();
      
    } catch (error) {
      console.error('Erreur lors de l\'attribution de la commande:', error);
      message.error('Erreur lors de l\'attribution de la commande');
    } finally {
      setSaving(false);
    }
  };

  // Navigation vers la commande précédente
  const goToPreviousOrder = () => {
    if (currentOrderIndex > 0) {
      const previousOrder = ordersList[currentOrderIndex - 1];
      // Conserver les filtres lors de la navigation
      const params = new URLSearchParams(location.search);
      navigate(`/app/orders/${previousOrder.id}?${params.toString()}`);
    }
  };

  // Navigation vers la commande suivante
  const goToNextOrder = () => {
    if (currentOrderIndex < ordersList.length - 1) {
      const nextOrder = ordersList[currentOrderIndex + 1];
      // Conserver les filtres lors de la navigation
      const params = new URLSearchParams(location.search);
      navigate(`/app/orders/${nextOrder.id}?${params.toString()}`);
    }
  };

  // Marquer la commande comme payée
  const handleMarkAsPaid = async () => {
    try {
      const values = await paymentForm.validateFields();
      setSaving(true);

      // Données du paiement avec frais de livraison
      const paymentData = {
        amount: values.amount,
        method: values.method,
        notes: values.notes || 'Paiement manuel depuis le backoffice',
        shipping_fee: values.shipping_fee || 0 // Frais de livraison (peut être modifié)
      };

      const response = await ordersApi.markOrderAsPaid(id, paymentData);
      console.log('response', response);

      if (!response) {
        throw new Error('Erreur lors de l\'enregistrement du paiement');
      }

      message.success('Paiement enregistré avec succès');
      setIsPaymentModalVisible(false);
      paymentForm.resetFields();
      
      // Recharger les détails de la commande
      await loadOrderDetails();
      
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement du paiement:', error);
      message.error('Erreur lors de l\'enregistrement du paiement');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Annule la commande en cours.
   * Cette fonction gère la mise à jour des notes de livraison pour inclure le motif d'annulation
   * sans écraser les notes existantes, et gère également le blocage optionnel du client.
   */
  const handleCancelOrder = async () => {
    try {
      // Valider les champs du formulaire d'annulation
      const values = await cancelForm.validateFields();
      setSaving(true);

      // Récupération des notes de livraison existantes pour ne pas les perdre
      let currentDeliveryNotes = order.delivery_notes || '';
      const cancelReason = values.reason?.trim();
      
      /**
       * Si un motif d'annulation est fourni, on le concatène aux notes de livraison
       * existantes avec un horodatage au format [JJ/MM/AAAA HH:mm:ss].
       */
      console.log('cancelReason', cancelReason);
      if (cancelReason) {
        const timestamp = dayjs().format('DD/MM/YYYY HH:mm:ss');
        const cancellationNote = `[${timestamp}] Annulation: ${cancelReason}`;
        console.log('cancellationNote', cancellationNote);
        // Ajout d'un saut de ligne si des notes existent déjà pour la lisibilité
        currentDeliveryNotes = currentDeliveryNotes 
          ? `${currentDeliveryNotes}\n${cancellationNote}`
          : cancellationNote;
        console.log('currentDeliveryNotes', currentDeliveryNotes);
      }

      // Préparation des données pour l'API d'annulation
      const cancelData = {
        reason: cancelReason || 'Annulation sans motif spécifié',
        block_customer: values.block_customer || false,
        delivery_notes: currentDeliveryNotes // On passe les notes complétées
      };
      console.log('cancelData', cancelData);
      // Appel à l'API serveur pour effectuer l'annulation
      const response = await ordersApi.cancelOrder(id, cancelData);
      console.log('Réponse annulation:', response);

      if (!response) {
        throw new Error('Erreur lors de l\'annulation de la commande');
      }

      // Message de succès personnalisé selon si le client a été bloqué ou non
      if (values.block_customer && response.data?.customer_blocked) {
        message.success('Commande annulée et client bloqué avec succès');
      } else {
        message.success('Commande annulée avec succès');
      }

      // Fermeture du modal et réinitialisation du formulaire
      setIsCancelModalVisible(false);
      cancelForm.resetFields();

      // Rechargement des détails pour mettre à jour l'affichage
      await loadOrderDetails();

    } catch (error) {
      console.error('Erreur lors de l\'annulation de la commande:', error);
      // On n'affiche pas de message d'erreur si c'est juste une validation de formulaire échouée
      if (error.errorFields) return;
      message.error('Erreur lors de l\'annulation de la commande');
    } finally {
      setSaving(false);
    }
  };

  // Ouvrir le modal de paiement
  const showPaymentModal = () => {
    // Pré-remplir avec le montant total ou le montant restant
    const remainingAmount = order.total_amount || 0;
    // Pré-remplir avec les frais de livraison existants
    const currentShippingFee = order.shipping_fee || 0;
    paymentForm.setFieldsValue({
      amount: remainingAmount,
      method: 'cash',
      shipping_fee: currentShippingFee
    });
    setIsPaymentModalVisible(true);
  };

  // Ouvrir le modal d'annulation
  const showCancelModal = () => {
    setIsCancelModalVisible(true);
  };

  // Rembourser la commande
  const handleRefundOrder = async () => {
    try {
      const values = await refundForm.validateFields();
      setSaving(true);

      // Données de remboursement
      const refundData = {
        amount: values.amount,
        reason: values.reason
      };
      
      const response = await ordersApi.refundOrder(id, refundData);
      console.log('response', response);

      if (!response) {
        throw new Error('Erreur lors du remboursement de la commande');
      }

      message.success('Remboursement traité avec succès');
      setIsRefundModalVisible(false);
      refundForm.resetFields();
      
      // Recharger les détails de la commande
      await loadOrderDetails();
      
    } catch (error) {
      console.error('Erreur lors du remboursement de la commande:', error);
      message.error('Erreur lors du remboursement de la commande');
    } finally {
      setSaving(false);
    }
  };

  // Ouvrir le modal de remboursement
  const showRefundModal = () => {
    // Pré-remplir avec le montant total
    refundForm.setFieldsValue({
      amount: order.total_amount,
    });
    setIsRefundModalVisible(true);
  };

  // Ouvrir le modal de report
  const showRescheduleModal = () => {
    // Pré-remplir avec la date de livraison actuelle si elle existe
    // Utiliser moment.utc() pour parser la date sans conversion de fuseau horaire
    rescheduleForm.setFieldsValue({
      delivery_date: order.delivery_date ? dayjs.utc(order.delivery_date) : null,
    });
    setIsRescheduleModalVisible(true);
  };

  // Reporter la commande (changer la date de livraison)
  const handleRescheduleOrder = async () => {
    try {
      const values = await rescheduleForm.validateFields();
      setSaving(true);

      // Données de report
      const rescheduleData = {
        delivery_date: values.delivery_date ? values.delivery_date.format('YYYY-MM-DD') : null
      };
      
      const response = await ordersApi.update(id, rescheduleData);
      console.log('response', response);

      if (!response) {
        throw new Error('Erreur lors du report de la commande');
      }

      message.success('Date de livraison mise à jour avec succès');
      setIsRescheduleModalVisible(false);
      rescheduleForm.resetFields();
      
      // Recharger les détails de la commande
      await loadOrderDetails();
      
    } catch (error) {
      console.error('Erreur lors du report de la commande:', error);
      message.error('Erreur lors du report de la commande');
    } finally {
      setSaving(false);
    }
  };

  // Réserver le stock (passer en prêt à livrer)
  const handleReserveStock = async () => {
    try {
      setReservingStock(true);
      
      const response = await ordersApi.reserveStock(id);
      
      if (response && response.data) {
        message.success('Stock réservé et commande marquée comme prête à livrer');
        await loadOrderDetails();
      } else {
        throw new Error('Erreur lors de la réservation du stock');
      }
      
    } catch (error) {
      console.error('Erreur lors de la réservation du stock:', error);
      message.error('Erreur lors de la réservation du stock');
    } finally {
      setReservingStock(false);
    }
  };

  // Charger les produits disponibles pour l'ajout
  const loadAvailableProducts = async (searchText = '') => {
    try {
      setLoadingProducts(true);
      const response = await productsApi.getAll({
        search: searchText,
        limit: 50
      });

      if (response && response.data) {
        setAvailableProducts(response.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      message.error('Erreur lors du chargement des produits');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Ouvrir le modal d'édition des items
  const showEditItemsModal = () => {
    // Initialiser la liste des items éditables avec les items actuels
    const initialItems = (order.order_items || []).map(item => ({
      id: item.id,
      product_variant_id: item.product_variant_id,
      product_name: item.product_name,
      variant_title: item.product_variant?.title,
      quantity: item.quantity,
      product_price: item.product_price,
      notes: item.notes || '',
      image_url: item.product_variant?.product?.main_image ||
                 item.product_variant?.image_url ||
                 item.shopify_product?.main_image,
      // Ajouter le stock disponible (quantité actuelle + stock de la variante)
      available_stock: (item.product_variant?.inventory_quantity || 0) + item.quantity,
      original_quantity: item.quantity // Pour calculer le delta de stock
    }));

    setEditableItems(initialItems);
    // Réinitialiser le texte de recherche pour charger la liste complète des produits
    setSearchProductText('');
    setIsEditItemsModalVisible(true);
    // Charger la liste initiale des produits (sera géré par le useEffect avec debounce)
  };

  // Ajouter un nouveau produit à la liste des items
  const handleAddProduct = (productVariantId) => {
    // Trouver le produit/variante sélectionné dans la liste des produits disponibles
    let selectedProduct = null;
    let selectedVariant = null;

    for (const product of availableProducts) {
      if (product.product_variants) {
        const variant = product.product_variants.find(v => v.id === productVariantId);
        if (variant) {
          selectedProduct = product;
          selectedVariant = variant;
          break;
        }
      }
    }

    if (!selectedVariant) {
      message.error('Produit non trouvé');
      return;
    }

    // Vérifier si le produit n'est pas déjà dans la liste
    const existingItem = editableItems.find(item => item.product_variant_id === productVariantId);
    if (existingItem) {
      message.warning('Ce produit est déjà dans la commande');
      return;
    }

    // Vérifier le stock disponible
    const availableStock = selectedVariant.inventory_quantity || 0;
    if (availableStock < 1) {
      message.error('Stock insuffisant pour ce produit');
      return;
    }

    // Ajouter le nouveau produit à la liste
    const newItem = {
      id: null, // Pas d'id = nouvel item
      product_variant_id: productVariantId,
      product_name: `${selectedProduct.name} - ${selectedVariant.title}`,
      variant_title: selectedVariant.title,
      quantity: 1,
      product_price: selectedVariant.price || 0,
      notes: '',
      image_url: selectedVariant.image_url || selectedProduct.main_image,
      available_stock: availableStock,
      original_quantity: 0 // Nouveau produit, donc quantité originale = 0
    };

    setEditableItems([...editableItems, newItem]);
    message.success('Produit ajouté');
  };

  // Supprimer un produit de la liste
  const handleRemoveItem = (index) => {
    const newItems = [...editableItems];
    newItems.splice(index, 1);
    setEditableItems(newItems);
    message.success('Produit retiré');
  };

  // Modifier la quantité d'un produit
  const handleQuantityChange = (index, newQuantity) => {
    if (newQuantity < 1) {
      message.error('La quantité doit être supérieure à 0');
      return;
    }

    const item = editableItems[index];
    const availableStock = item.available_stock || 0;

    // Vérifier que la nouvelle quantité ne dépasse pas le stock disponible
    if (newQuantity > availableStock) {
      message.error(`Stock insuffisant. Maximum disponible : ${availableStock}`);
      return;
    }

    const newItems = [...editableItems];
    newItems[index].quantity = newQuantity;
    setEditableItems(newItems);
  };

  // Modifier le prix d'un produit
  const handlePriceChange = (index, newPrice) => {
    if (newPrice < 0) {
      message.error('Le prix ne peut pas être négatif');
      return;
    }

    const newItems = [...editableItems];
    newItems[index].product_price = newPrice;
    setEditableItems(newItems);
  };

  // Sauvegarder les modifications des items
  const handleSaveOrderItems = async () => {
    try {
      // Vérifier qu'il y a au moins un item
      if (editableItems.length === 0) {
        message.error('La commande doit contenir au moins un produit');
        return;
      }

      setSaving(true);

      // Préparer les données pour l'API
      const items = editableItems.map(item => ({
        id: item.id,
        product_variant_id: item.product_variant_id,
        quantity: item.quantity,
        product_price: item.product_price,
        product_name: item.product_name,
        notes: item.notes
      }));

      // Appeler l'API pour mettre à jour les items
      const response = await ordersApi.updateOrderItems(order.id, items);

      if (response && response.data) {
        message.success('Articles de la commande mis à jour avec succès');
        setIsEditItemsModalVisible(false);
        // Recharger les détails de la commande
        await loadOrderDetails();
      } else {
        throw new Error('Erreur lors de la mise à jour des items');
      }

    } catch (error) {
      console.error('Erreur lors de la mise à jour des items:', error);

      // Gérer les erreurs de stock spécifiques
      if (error.response && error.response.data) {
        const errorData = error.response.data;

        // Si c'est une erreur de stock, afficher les détails
        if (errorData.details && Array.isArray(errorData.details)) {
          const stockErrorMessages = errorData.details.map(detail =>
            `${detail.productName}: ${detail.error}`
          );

          Modal.error({
      // Conserve la portée du thème privé dans cette confirmation statique.
      rootClassName: "private-ui",
            title: 'Stock insuffisant',
            content: (
              <div>
                <p>Les produits suivants n'ont pas assez de stock disponible :</p>
                <ul>
                  {stockErrorMessages.map((msg, idx) => (
                    <li key={idx}>{msg}</li>
                  ))}
                </ul>
              </div>
            )
          });
        } else {
          message.error(errorData.error || 'Erreur lors de la mise à jour des articles de la commande');
        }
      } else {
        message.error('Erreur lors de la mise à jour des articles de la commande');
      }
    } finally {
      setSaving(false);
    }
  };

  // Vérifier si la commande peut avoir ses items modifiés
  const canEditItems = () => {
    return order &&
           order.status !== 'cancelled' &&
           order.status !== 'delivered' &&
           order.status !== 'refunded' &&
           order.status !== 'completed';
  };

  // Vérifier si la commande peut être payée
  const canMarkAsPaid = () => {
    return order &&
           order.status !== 'canceled' &&
           order.status !== 'cancelled' &&
           order.status !== 'completed' &&
           order.status !== 'delivered' &&
           order.status !== 'refunded';
  };

  // Vérifier si la commande peut être annulée
  const canCancelOrder = () => {
    return order && 
           order.status !== 'cancelled' && 
           order.status !== 'completed' &&
           order.status !== 'delivered' &&
           order.status !== 'refunded';
  };

  // Vérifier si la commande peut être remboursée (si elle est completed, c'est qu'elle est payée)
  const canRefundOrder = () => {
    return order && order.status === 'completed';
  };

  // Vérifier si la commande peut être reportée
  const canRescheduleOrder = () => {
    return order && 
           order.status !== 'cancelled' && 
           order.status !== 'completed' &&
           order.status !== 'delivered' &&
           order.status !== 'refunded';
  };

  // Vérifier si le stock peut être réservé
  const canReserveStock = () => {
    return order && ['new', 'confirmed', 'preparing'].includes(order.status);
  };

  // Copier le lien de la commande dans le presse-papiers
  const handleCopyOrderLink = async () => {
    try {
      // URL du site (à configurer dans .env ou récupérer des settings)
      // Par défaut, utiliser window.location.origin si pas configuré
      const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
      const orderLink = `${siteUrl}/order-view/${order.id}`;

      // Copier dans le presse-papiers
      await navigator.clipboard.writeText(orderLink);

      message.success('Lien de la commande copié dans le presse-papiers');
    } catch (error) {
      console.error('Erreur lors de la copie du lien:', error);
      message.error('Erreur lors de la copie du lien');
    }
  };

  /**
   * Envoie le lien de feedback par WhatsApp au client
   * Disponible uniquement pour les commandes avec statut 'delivered' ou 'completed'
   */
  const handleSendFeedbackLink = async () => {
    try {
      // Vérifier que le client a un numéro de téléphone
      if (!order.customer?.phone) {
        message.error('Le client n\'a pas de numéro de téléphone enregistré');
        return;
      }

      setSendingFeedbackLink(true);

      // Construire le lien de feedback
      const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
      const feedbackLink = `${siteUrl}/feedback/${order.id}`;

      // Construire le message WhatsApp avec le lien de feedback
      const customerName = order.customer?.name || 'Cher client';
      const messageContent = `Bonjour! 🎉\n\nMerci pour votre commande.\n\nNous espérons que vous êtes satisfait(e) de votre achat.\n\n📝 Laissez-nous votre avis ici : ${feedbackLink}\n\nMerci de votre confiance ! 😊`;

      // Envoyer le message via l'API WhatsApp
      const response = await whatsappApi.sendMessage({
        phone: order.customer.phone,
        message: messageContent
      });

      if (response.status === 200) {
        message.success('Lien de feedback envoyé par WhatsApp avec succès !');
      } else {
        throw new Error('Erreur lors de l\'envoi du message');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du lien de feedback:', error);
      message.error('Erreur lors de l\'envoi du message WhatsApp. Veuillez réessayer.');
    } finally {
      setSendingFeedbackLink(false);
    }
  };

  // Vérifier si le bouton d'envoi de feedback WhatsApp doit être affiché
  // Affiché uniquement pour les commandes 'delivered' ou 'completed'
  const canSendFeedbackLink = () => {
    return order &&
           (order.status === 'delivered' || order.status === 'completed') &&
           order.customer?.phone;
  };

  // Menu dropdown pour les autres actions
  const getActionsMenu = () => {
    // Si la commande est remboursée, ne pas afficher le menu d'actions
    if (order && (order.status === 'refunded' || order.status === 'completed')) {
      return <Menu />;
    }

    return (
      <Menu>
        {canReserveStock() && (
          <Menu.Item
            key="reserve-stock"
            icon={reservingStock ? <LoadingOutlined /> : <ShoppingOutlined />}
            onClick={handleReserveStock}
            disabled={reservingStock}
          >
            Réserver le stock
          </Menu.Item>
        )}
        {canEditItems() && (
          <Menu.Item
            key="edit-items"
            icon={<EditOutlined />}
            onClick={showEditItemsModal}
          >
            Modifier les produits
          </Menu.Item>
        )}
        <Menu.Item
          key="edit"
          icon={<EditOutlined />}
          onClick={handleEditClick}
        >
          Modifier la commande
        </Menu.Item>
        {canRefundOrder() && (
          <Menu.Item
            key="refund"
            icon={<DollarOutlined />}
            onClick={showRefundModal}
          >
            Rembourser
          </Menu.Item>
        )}
        {canCancelOrder() && (
          <Menu.Item
            key="cancel"
            icon={<StopOutlined />}
            onClick={showCancelModal}
            danger
          >
            Annuler
          </Menu.Item>
        )}
      </Menu>
    );
  };

  /**
   * Effet pour charger les détails de la commande et la liste de navigation
   * à chaque changement de l'ID de la commande dans l'URL.
   */
  useEffect(() => {
    if (id) {
      loadOrderDetails(); // Charge les données complètes de la commande
      loadOrdersList();   // Charge la liste pour la navigation précédent/suivant
    }
  }, [id]);

  // Recharger la liste de navigation quand les filtres dans l'URL changent
  // useEffect(() => {
  //   if (id) {
  //     loadOrdersList();
  //   }
  // }, [location.search, id]);

  /**
   * Effet pour la recherche asynchrone des produits avec anti-rebond (debounce).
   * Attend 1000ms après la dernière saisie avant de lancer la recherche API
   * pour économiser les ressources serveur.
   */
  useEffect(() => {
    // Création du minuteur pour le délai de 1 seconde
    const debounceTimer = setTimeout(() => {
      // On ne lance la recherche que si le modal d'édition est ouvert
      if (isEditItemsModalVisible && searchProductText !== undefined) {
        loadAvailableProducts(searchProductText);
      }
    }, 1000);

    // Nettoyage du minuteur si l'effet est redéclenché (l'utilisateur continue de taper)
    return () => clearTimeout(debounceTimer);
  }, [searchProductText, isEditItemsModalVisible]);

  // Couleur du statut de commande
  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'new': return 'blue';
      case 'pending': return 'warning';
      case 'confirmed': return 'processing';
      case 'in_delivery': return 'cyan';
      case 'delivered': return 'success';
      case 'canceled': return 'error';
      case 'cancelled': return 'error';
      case 'completed': return 'success';
      case 'refunded': return 'purple';
      default: return 'default';
    }
  };

  // Traduction du statut
  const getStatusLabel = (status) => {
    switch (status) {
      case 'new': return 'Nouveau';
      case 'pending': return 'En attente';
      case 'confirmed': return 'Confirmé';
      case 'in_delivery': return 'En livraison';
      case 'delivered': return 'Livré';
      case 'canceled': return 'Annulé';
      case 'cancelled': return 'Annulé';
      case 'completed': return 'Complété';
      case 'refunded': return 'Remboursé';
      default: return status;
    }
  };

  // Vérifier si des données UTM sont présentes dans custom_data
  const hasUtmData = (order) => {
    if (!order?.custom_data) return false;
    const utmFields = ['utm_campaign', 'utm_content', 'utm_medium', 'utm_source', 'utm_term'];
    return utmFields.some(field => order.custom_data[field]);
  };

  // Extraire les données UTM depuis custom_data
  const getUtmData = (order) => {
    if (!order?.custom_data) return {};
    
    const utmFields = ['utm_campaign', 'utm_content', 'utm_medium', 'utm_source', 'utm_term'];
    const utmData = {};
    
    utmFields.forEach(field => {
      if (order.custom_data[field]) {
        utmData[field] = order.custom_data[field];
      }
    });
    
    return utmData;
  };

  // Obtenir le libellé français pour les paramètres UTM
  const getUtmLabel = (key) => {
    const labels = {
      utm_campaign: 'Campagne',
      utm_content: 'Contenu',
      utm_medium: 'Support',
      utm_source: 'Source',
      utm_term: 'Terme'
    };
    return labels[key] || key;
  };

  // Colonnes pour le tableau des articles
  const itemsColumns = [
    {
      title: 'Article',
      dataIndex: 'product_name',
      key: 'product_name',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          {/* Image du produit - Utilise les nouvelles relations */}
          {(record.product_variant?.product?.main_image || 
            record.product_variant?.image_url ||
            record.shopify_product?.main_image) && (
            <Image
              width={60}
              height={60}
              // L'image garde sa taille même si le nom du produit est long
              wrapperStyle={{ flexShrink: 0 }}
              src={
                record.product_variant?.product?.main_image || 
                record.product_variant?.image_url ||
                record.shopify_product?.main_image
              }
              alt="Produit"
              style={{ 
                objectFit: 'cover', 
                marginRight: '12px', 
                borderRadius: '6px',
                border: '1px solid #f0f0f0'
              }}
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
            />
          )}
          {/* minWidth: 0 permet au texte long de passer à la ligne au lieu de déborder */}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 500, wordBreak: 'break-word' }}>
              {/* Priorité: product_name de l'item, puis nom du produit, puis titre du variant */}
              {text || 
               record.product_variant?.product?.name || 
               record.product_variant?.title ||
               'Article inconnu'}
            </div>
            {/* Variation de l'article (taille, couleur, etc.) */}
            {getVariantLabel(text || record.product_variant?.product?.name, record.product_variant?.title) && (
              <Tag color="geekblue" style={{ marginBottom: 4 }} className="private-tag-accent" >
                {getVariantLabel(text || record.product_variant?.product?.name, record.product_variant?.title)}
              </Tag>
            )}
            {/* Afficher le SKU et autres informations du variant */}
            {record.product_variant?.sku && (
              <Text code style={{ fontSize: '12px' }}>{record.product_variant.sku}</Text>
            )}
            {/* Afficher le type de produit si disponible */}
            {record.product_variant?.product?.product_type && (
              <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>
                {record.product_variant.product.product_type}
              </Text>
            )}
            {/* Vendor + statut regroupés pour éviter le chevauchement */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
            {/* Afficher le vendor si disponible */}
            {record.product_variant?.product?.vendor && (
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {record.product_variant.product.vendor}
              </Text>
            )}
            {/* Notes spécifiques à l'article */}
            {record.notes && (
              <Text italic style={{ display: 'block', color: '#888', fontSize: '12px' }}>
                Note: {record.notes}
              </Text>
            )}
            {/* Statut de l'article si différent de pending */}
            {record.status && record.status !== 'pending' && (
              <Tag size="small" color={
                record.status === 'ready' ? 'green' : 
                record.status === 'in_kitchen' ? 'orange' : 
                record.status === 'served' ? 'blue' : 'default'
              }>
                {record.status}
              </Tag>
            )}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Prix unitaire',
      dataIndex: 'product_price',
      key: 'product_price',
      align: 'right',
      render: (price) => `${Number(price || 0).toFixed(2)} ${getDeviseActive()}`,
    },
    {
      title: 'Quantité',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center',
      render: (qty) => <Tag color="blue" className="private-tag-accent" >{qty}</Tag>,
    },
    {
      title: 'Total',
      key: 'total',
      align: 'right',
      render: (_, record) => {
        const total = (record.product_price || 0) * (record.quantity || 0);
        return <Text strong>{total.toFixed(2)} {getDeviseActive()}</Text>;
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        <div style={{ marginTop: '16px' }}>
          <Text>Chargement des détails de la commande...</Text>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text type="danger">Commande non trouvée</Text>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* En-tête avec breadcrumb et bouton retour - Version responsive */}
      <div className="mb-6">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            {
              href: '/app/dashboard',
              title: <HomeOutlined />,
            },
            {
              href: '/app/orders',
              title: 'Commandes',
            },
            {
              title: `#${order.order_number || order.id}`,
            },
          ]}
          className="mb-3 text-xs sm:text-sm"
        />
        
        {/* Titre et navigation */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <Title level={window.innerWidth < 768 ? 4 : 2} className="m-0">
            <span className="hidden sm:inline">Détails de la </span>Commande #{order.order_number || order.id}
          </Title>
          
          {/* Navigation entre commandes */}
          {ordersList.length > 0 && (
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
              <Button
                icon={<LeftOutlined />}
                size="small"
                disabled={currentOrderIndex <= 0 || loadingNavigation}
                onClick={goToPreviousOrder}
                title="Commande précédente"
              />
              <span className="px-2">
                {currentOrderIndex + 1} / {ordersList.length}
              </span>
              <Button
                icon={<RightOutlined />}
                size="small"
                disabled={currentOrderIndex >= ordersList.length - 1 || loadingNavigation}
                onClick={goToNextOrder}
                title="Commande suivante"
              />
            </div>
          )}
        </div>
        
        {/* Actions - Responsive */}
        <div className="flex flex-wrap gap-2 sm:gap-3">
          {canMarkAsPaid() && (
            <Button 
              icon={<DollarOutlined />} 
              type="primary"
              onClick={showPaymentModal}
              size={window.innerWidth < 768 ? "middle" : "large"}
              className="flex-1 sm:flex-none"
            >
              <span className="hidden sm:inline">Marquer comme </span>Payé
            </Button>
          )}
          {canRefundOrder() && (
            <Button 
              icon={<DollarOutlined />} 
              type="primary" 
              danger
              onClick={showRefundModal}
              size={window.innerWidth < 768 ? "middle" : "large"}
              className="flex-1 sm:flex-none"
            >
              Rembourser
            </Button>
          )}
          {(order.status === 'new' || order.status === 'confirmed') && (
            <Button 
              icon={<CarOutlined />} 
              type="default"
              onClick={showAssignmentModal}
              size={window.innerWidth < 768 ? "middle" : "large"}
              className="flex-1 sm:flex-none"
            >
              <span className="hidden sm:inline">Attribuer </span>Livreur
            </Button>
          )}
          {canRescheduleOrder() && (
            <Button
              icon={<CalendarOutlined />}
              type="default"
              onClick={showRescheduleModal}
              size={window.innerWidth < 768 ? "middle" : "large"}
              className="flex-1 sm:flex-none"
            >
              <span className="hidden sm:inline">Reporter </span>Livraison
            </Button>
          )}
          <Button
            icon={<LinkOutlined />}
            type="default"
            onClick={handleCopyOrderLink}
            size={window.innerWidth < 768 ? "middle" : "large"}
            className="flex-1 sm:flex-none"
          >
            <span className="hidden sm:inline">Copier </span>Lien
          </Button>
          {/* Bouton d'envoi d'un message WhatsApp au client (messages prédéfinis : injoignable, sans réponse...) */}
          {order.customer?.phone && (
            <Button
              icon={<WhatsAppOutlined style={{ color: '#25D366' }} />}
              type="default"
              onClick={() => setIsWhatsAppModalVisible(true)}
              size={window.innerWidth < 768 ? "middle" : "large"}
              className="flex-1 sm:flex-none"
            >
              <span className="hidden sm:inline">Message </span>Client
            </Button>
          )}
          {/* Bouton WhatsApp pour envoyer le lien de feedback - visible uniquement pour les commandes delivered/completed */}
          {canSendFeedbackLink() && (
            <Button
              icon={<WhatsAppOutlined />}
              type="primary"
              onClick={handleSendFeedbackLink}
              loading={sendingFeedbackLink}
              size={window.innerWidth < 768 ? "middle" : "large"}
              className="flex-1 sm:flex-none"
              style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
            >
              <span className="hidden sm:inline">Demander </span>Avis
            </Button>
          )}
          <WhatsAppProviderBadge />
          {order && (order.status !== 'refunded' && order.status !== 'completed') && (
            <Dropdown overlay={getActionsMenu()} trigger={['click']}>
              <Button
                icon={<MoreOutlined />}
                size={window.innerWidth < 768 ? "middle" : "large"}
                className="flex-1 sm:flex-none"
              >
                <span className="hidden sm:inline">Autres </span>Actions
              </Button>
            </Dropdown>
          )}
          <Button
            icon={<ArrowLeftOutlined />} 
            onClick={() => {
              // Récupérer les filtres depuis les paramètres d'URL s'ils existent
              const params = new URLSearchParams(location.search);
              const returnFilters = params.get('returnFilters');
              
              if (returnFilters) {
                // Restaurer les filtres précédents
                navigate(`/app/orders?${decodeURIComponent(returnFilters)}`);
              } else {
                // Retour simple vers l'onglet orders
                navigate('/app/orders?tab=orders');
              }
            }}
            size={window.innerWidth < 768 ? "middle" : "large"}
            className="flex-1 sm:flex-none"
          >
            <span className="sm:hidden">Retour</span>
            <span className="hidden sm:inline">Retour à la liste</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Informations générales */}
        <div>
          <Card title="Informations générales" className="h-full">
            <Descriptions 
              column={1} 
              bordered 
              size={window.innerWidth < 768 ? "small" : "middle"}
              labelStyle={{ 
                width: window.innerWidth < 768 ? '40%' : '30%',
                fontSize: window.innerWidth < 768 ? '12px' : '14px'
              }}
              contentStyle={{
                fontSize: window.innerWidth < 768 ? '12px' : '14px'
              }}
            >
              <Descriptions.Item label="N° commande">
                <Text strong className="text-xs sm:text-sm">{order.order_number || 'N/A'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                <Tag color={getOrderStatusColor(order.status)} className="text-xs">
                  {getStatusLabel(order.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Source">
                <Tag color={order.source === 'shopify' ? 'green' : 'blue'} className="text-xs">
                  {order.source || 'Manuel'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Montant total">
                <Text strong className="text-sm sm:text-base text-blue-600">
                  {Number(order.total_amount || 0).toFixed(0)} {getDeviseActive()}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Date de création">
                <span className="text-xs sm:text-sm">
                  {dayjs(order.created_at).format('DD/MM/YYYY [à] HH:mm')}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Dernière modification">
                <span className="text-xs sm:text-sm">
                  {dayjs(order.updated_at).format('DD/MM/YYYY [à] HH:mm')}
                </span>
              </Descriptions.Item>
              {order.delivery_date && (
                <Descriptions.Item label="Date de livraison prévue">
                  <span className="text-xs sm:text-sm">
                    {/* Parse la date sans conversion de fuseau horaire pour afficher la date exacte */}
                    {dayjs.utc(order.delivery_date).format('DD/MM/YYYY')}
                  </span>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </div>

        {/* Informations client et livraison */}
        <div>
          <Card title="Client et Livraison" className="h-full">
            <Descriptions 
              column={1} 
              bordered 
              size={window.innerWidth < 768 ? "small" : "middle"}
              labelStyle={{ 
                width: window.innerWidth < 768 ? '40%' : '30%',
                fontSize: window.innerWidth < 768 ? '12px' : '14px'
              }}
              contentStyle={{
                fontSize: window.innerWidth < 768 ? '12px' : '14px'
              }}
            >
              <Descriptions.Item label="Nom du client">
                <Text strong className="text-xs sm:text-sm">{order.customer?.name || 'Client inconnu'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Téléphone">
                <span className="text-xs sm:text-sm">{order.customer?.phone || 'N/A'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                <span className="text-xs sm:text-sm break-all">{order.customer?.email || 'N/A'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Livreur assigné">
                {order.delivery_person ? (
                  <div>
                    <Text strong className="text-xs sm:text-sm">{order.delivery_person.name}</Text>
                    {order.delivery_person.phone && (
                      <div className="text-xs text-gray-500 mt-1">
                        {order.delivery_person.phone}
                      </div>
                    )}
                  </div>
                ) : (
                  <Text type="secondary" italic className="text-xs sm:text-sm">Non assigné</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Adresse de livraison">
                <Text className="text-xs sm:text-sm whitespace-pre-wrap break-words">
                  {order.delivery_address || 'Non fournie'}
                </Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </div>
      </div>

      {/* Notes de livraison */}
      {order.delivery_notes && (
        <Card title="Notes de livraison" style={{ marginTop: '24px' }}>
          <Text style={{ whiteSpace: 'pre-wrap' }}>{order.delivery_notes}</Text>
        </Card>
      )}

      {/* Notes générales de la commande - Affichage conditionnel si notes disponibles */}
      {order.notes && (
        <Card title="Notes de la commande" style={{ marginTop: '24px' }}>
          <Text style={{ whiteSpace: 'pre-wrap' }}>{order.notes}</Text>
        </Card>
      )}

      {/* Section des données UTM - affichage conditionnel */}
      {hasUtmData(order) && (
        <Card title="Données de suivi (UTM)" className="mt-6">
          <Descriptions 
            column={window.innerWidth < 768 ? 1 : 2} 
            bordered 
            size={window.innerWidth < 768 ? "small" : "middle"}
            labelStyle={{ 
              width: window.innerWidth < 768 ? '40%' : '30%',
              fontSize: window.innerWidth < 768 ? '12px' : '14px'
            }}
            contentStyle={{
              fontSize: window.innerWidth < 768 ? '12px' : '14px'
            }}
          >
            {Object.entries(getUtmData(order)).map(([key, value]) => (
              <Descriptions.Item key={key} label={getUtmLabel(key)}>
                <Text className="text-xs sm:text-sm" code style={{ backgroundColor: 'var(--private-neutral-surface, #f6f6f6)' }}>
                  {value}
                </Text>
              </Descriptions.Item>
            ))}
          </Descriptions>
        </Card>
      )}

      {/* Articles de la commande */}
      <Card 
        title="Articles commandés" 
        className="mt-6"
        bodyStyle={{ padding: window.innerWidth < 768 ? '12px' : '24px' }}
      >
        {isMobile ? (
          /* Mobile : une carte par article (nom + variation en haut, puis prix/quantité/total) et total général */
          <ColumnsCardList
            columns={itemsColumns}
            dataSource={order.order_items || []}
            headerKeys={['product_name']}
            emptyText="Aucun article"
            footer={(order.order_items || []).length > 0 && (
              <div className="mt-3 flex items-center justify-between rounded-lg bg-blue-50 p-3">
                <Text strong>Total</Text>
                <Text strong className="text-blue-600">
                  {(order.order_items || []).reduce((sum, item) => sum + (item.product_price || 0) * (item.quantity || 0), 0).toFixed(0)} {getDeviseActive()}
                </Text>
              </div>
            )}
          />
        ) : (
        <Table
          columns={itemsColumns}
          dataSource={order.order_items || []}
          rowKey="id"
          pagination={false}
          scroll={{ 
            x: 'max-content',
            scrollToFirstRowOnChange: true 
          }}
          size={window.innerWidth < 768 ? 'small' : 'middle'}
          summary={(pageData) => {
            const total = pageData.reduce(
              (sum, item) => sum + (item.product_price || 0) * (item.quantity || 0),
              0
            );
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Text strong className="text-xs sm:text-sm">Total</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong className="text-sm sm:text-base text-blue-600">
                    {total.toFixed(0)} {getDeviseActive()}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
        )}
        
        {(!order.order_items || order.order_items.length === 0) && (
          <div className="text-center py-10">
            <Text type="secondary" className="text-xs sm:text-sm">
              Aucun article trouvé pour cette commande
            </Text>
          </div>
        )}
      </Card>

      {/* Modal de modification */}
      <Modal
        title="Modifier la commande"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        width={window.innerWidth < 768 ? '95vw' : 600}
        style={window.innerWidth < 768 ? { top: 20 } : {}}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            name="status"
            label="Statut de la commande"
            rules={[{ required: true, message: 'Veuillez sélectionner un statut' }]}
          >
            <Select placeholder="Sélectionner un statut">
              <Select.Option value="new">Nouveau</Select.Option>
              <Select.Option value="confirmed">Confirmé</Select.Option>
              <Select.Option value="preparing">En préparation</Select.Option>
              <Select.Option value="ready_to_deliver">Prêt à livrer</Select.Option>
              <Select.Option value="in_delivery">En livraison</Select.Option>
              <Select.Option value="delivered">Livré</Select.Option>
              <Select.Option value="completed">Complété</Select.Option>
              <Select.Option value="cancelled">Annulé</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="delivery_person_id"
            label="Livreur assigné"
          >
            {/* Recherche textuelle et défilement natif sur mobile, avec une liste ancrée dans le modal. */}
            <Select 
              {...getDeliveryPersonSelectProps(isMobile)}
              placeholder="Sélectionner un livreur (optionnel)" 
              allowClear
              loading={loadingDeliveryPersons}
              showSearch
              optionFilterProp="searchText"
            >
              {deliveryPersons.map(person => (
                <Select.Option key={person.id} value={person.id} searchText={`${person.name || ''} ${person.phone || ''}`}>
                  {person.name} {person.phone && `(${person.phone})`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="delivery_notes"
            label="Notes de livraison"
          >
            <Input.TextArea
              rows={3}
              placeholder="Notes particulières pour la livraison..."
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                onClick={() => setIsEditModalVisible(false)}
                icon={<CloseOutlined />}
              >
                Annuler
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={saving}
                icon={<SaveOutlined />}
              >
                Sauvegarder
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal d'attribution de livreur */}
      <Modal
        title="Attribuer un livreur à la commande"
        open={isAssignmentModalVisible}
        onCancel={() => setIsAssignmentModalVisible(false)}
        onOk={handleAssignOrder}
        okText="Attribuer"
        cancelText="Annuler"
        confirmLoading={saving}
        width={window.innerWidth < 768 ? '95vw' : 600}
        style={window.innerWidth < 768 ? { top: 20 } : {}}
      >
        <Form
          form={assignmentForm}
          layout="vertical"
        >
          <Form.Item
            label="Livreur"
            name="delivery_person_id"
            rules={[{ required: true, message: 'Veuillez sélectionner un livreur' }]}
          >
            {/* Recherche textuelle et liste tactile stable, même avec les icônes et statistiques. */}
            <Select
              {...getDeliveryPersonSelectProps(isMobile)}
              placeholder="Sélectionner un livreur"
              loading={loadingDeliveryPersons}
              showSearch
              optionFilterProp="searchText"
            >
              {deliveryPersons.map(person => {
                const stats = deliveryPersonsStats[person.id] || { orders_count: 0 };
                return (
                  <Select.Option key={person.id} value={person.id} searchText={`${person.name || ''} ${person.phone || ''}`}>
                    <Space>
                      <UserOutlined />
                      <span>{person.name}</span>
                      {person.phone && <span style={{ color: '#666' }}>({person.phone})</span>}
                      <Tag color={stats.orders_count > 10 ? 'orange' : 'green'}>
                        {stats.orders_count} colis aujourd'hui
                      </Tag>
                    </Space>
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            label="Date de livraison"
            name="delivery_date"
            tooltip="Laissez vide pour utiliser la date par défaut"
          >
            <DatePicker 
              style={{ width: '100%' }} 
              format="DD/MM/YYYY"
              placeholder="Sélectionner une date (optionnel)"
              defaultPickerValue={dayjs()}
              disabledDate={(current) => {
                if (!current) return true;
                
                // Convertir en dayjs pour une manipulation cohérente
                const currentDay = dayjs(current);
                const today = dayjs().startOf('day');
                const maxDate = dayjs().add(7, 'days').endOf('day');
                
                // Vérifier si la date est dans la plage autorisée (aujourd'hui à +7 jours)
                return !currentDay.isBetween(today, maxDate, 'day', '[]');
              }}
              showToday={true}
              allowClear={true}
            />
          </Form.Item>

          <Form.Item
            label="Frais de livraison"
            name="shipping_fee"
            tooltip="Frais de livraison à appliquer"
          >
            <Input 
              type="number" 
              min={0} 
              step={500}
              addonAfter={getDeviseActive()}
              placeholder="Frais de livraison"
            />
          </Form.Item>

          <Form.Item
            label="Message au client"
            name="delivery_message_mode"
            tooltip="Choisissez l'envoi immédiat, la programmation habituelle à 09h00 ou l'absence de message"
          >
            <Radio.Group>
              <Space direction="vertical">
                <Radio value="scheduled">Programmer comme d’habitude</Radio>
                <Radio value="immediate">Envoyer maintenant au client</Radio>
                <Radio value="none">Ne pas envoyer de message au client</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          <div style={{ marginBottom: 16, padding: '12px', backgroundColor: 'var(--private-neutral-surface, #f6f6f6)', borderRadius: '6px' }}>
            <Text strong>Commande à attribuer :</Text>
            <div style={{ marginTop: '8px' }}>
              <Text>N° {order?.order_number || order?.id}</Text><br />
              <Text>Client : {order?.customer?.name || 'N/A'}</Text><br />
              <Text>Montant : {Number(order?.total_amount || 0).toFixed(2)} {getDeviseActive()}</Text>
            </div>
          </div>
        </Form>
      </Modal>

      {/* Modal de paiement */}
      <Modal
        title="Marquer la commande comme payée"
        open={isPaymentModalVisible}
        onCancel={() => setIsPaymentModalVisible(false)}
        footer={null}
        width={window.innerWidth < 768 ? '95vw' : 500}
        style={window.innerWidth < 768 ? { top: 20 } : {}}
      >
        <Form
          form={paymentForm}
          layout="vertical"
          onFinish={handleMarkAsPaid}
        >
          <div style={{ marginBottom: 16, padding: '12px', backgroundColor: 'var(--private-neutral-surface, #f6f6f6)', borderRadius: '6px' }}>
            <Text strong>Commande #{order?.order_number || order?.id}</Text><br />
            <Text>Montant total : {Number(order?.total_amount || 0).toFixed(2)} {getDeviseActive()}</Text><br />
            <Text>Statut actuel : </Text>
            <Tag color={order?.payment_status === 'pending' ? 'orange' : 'blue'}>
              {order?.payment_status === 'pending' ? 'En attente' : 
               order?.payment_status === 'partially_paid' ? 'Partiellement payé' : 
               order?.payment_status}
            </Tag>
          </div>

          <Form.Item
            name="amount"
            label="Montant du paiement"
            rules={[
              { required: true, message: 'Veuillez saisir le montant' },
              { type: 'number', min: 0.01, message: 'Le montant doit être supérieur à 0' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={`Montant en ${getDeviseActive()}`}
              precision={2}
              step={100}
              addonAfter={getDeviseActive()}
            />
          </Form.Item>

          <Form.Item
            name="method"
            label="Méthode de paiement"
            rules={[{ required: true, message: 'Veuillez sélectionner une méthode' }]}
          >
            <Select placeholder="Sélectionner une méthode de paiement">
              <Select.Option value="cash">Espèces</Select.Option>
              <Select.Option value="credit_card">Carte bancaire</Select.Option>
              <Select.Option value="bank_transfer">Virement bancaire</Select.Option>
              <Select.Option value="mobile_money">Mobile Money</Select.Option>
              <Select.Option value="check">Chèque</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="shipping_fee"
            label="Frais de livraison"
            tooltip="Vous pouvez modifier les frais de livraison si nécessaire"
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={`Frais de livraison en ${getDeviseActive()}`}
              precision={0}
              step={500}
              min={0}
              addonAfter={getDeviseActive()}
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes (optionnel)"
          >
            <Input.TextArea
              rows={3}
              placeholder="Notes sur le paiement..."
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                onClick={() => setIsPaymentModalVisible(false)}
                icon={<CloseOutlined />}
              >
                Annuler
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={saving}
                icon={<SaveOutlined />}
              >
                Enregistrer le paiement
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal d'annulation */}
      <Modal
        title="Annuler la commande"
        open={isCancelModalVisible}
        onCancel={() => setIsCancelModalVisible(false)}
        footer={null}
        width={window.innerWidth < 768 ? '95vw' : 500}
        style={window.innerWidth < 768 ? { top: 20 } : {}}
      >
        <div style={{ marginBottom: 16 }}>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
          <Text strong>Attention !</Text>
          <Text style={{ display: 'block', marginTop: 8 }}>
            Cette action annulera définitivement la commande. Le client sera automatiquement notifié par WhatsApp.
          </Text>
        </div>

        <div style={{ marginBottom: 16, padding: '12px', backgroundColor: 'var(--private-neutral-surface, #f6f6f6)', borderRadius: '6px' }}>
          <Text strong>Commande à annuler :</Text><br />
          <Text>N° {order?.order_number || order?.id}</Text><br />
          <Text>Client : {order?.customer?.name || 'N/A'}</Text><br />
          <Text>Montant : {Number(order?.total_amount || 0).toFixed(2)} {getDeviseActive()}</Text><br />
          <Text>Statut : </Text>
          <Tag color={getOrderStatusColor(order?.status)}>
            {getStatusLabel(order?.status)}
          </Tag>
        </div>

        <Form
          form={cancelForm}
          layout="vertical"
          onFinish={handleCancelOrder}
        >
          <Form.Item
            name="reason"
            label="Motif d'annulation (optionnel)"
            rules={[{ required: false }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Expliquez pourquoi vous annulez cette commande (optionnel)..."
            />
          </Form.Item>

          <Form.Item
            name="block_customer"
            valuePropName="checked"
            style={{ marginBottom: 16 }}
          >
            <Checkbox>
              <span style={{ fontWeight: 500 }}>Bloquer ce client</span>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Ce client sera ajouté à la liste des clients non répondants et ne pourra plus passer de commandes
              </div>
            </Checkbox>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                onClick={() => setIsCancelModalVisible(false)}
                icon={<CloseOutlined />}
              >
                Fermer
              </Button>
              <Button 
                type="primary" 
                danger
                htmlType="submit"
                loading={saving}
                icon={<StopOutlined />}
              >
                Confirmer l'annulation
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de remboursement */}
      <Modal
        title="Rembourser la commande"
        open={isRefundModalVisible}
        onCancel={() => setIsRefundModalVisible(false)}
        footer={null}
        width={window.innerWidth < 768 ? '95vw' : 500}
        style={window.innerWidth < 768 ? { top: 20 } : {}}
      >
        <div style={{ marginBottom: 16 }}>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
          <Text strong>Attention !</Text>
          <Text style={{ display: 'block', marginTop: 8 }}>
            Cette action effectuera un remboursement pour cette commande. Le client sera automatiquement notifié par WhatsApp.
          </Text>
        </div>

        <div style={{ marginBottom: 16, padding: '12px', backgroundColor: 'var(--private-neutral-surface, #f6f6f6)', borderRadius: '6px' }}>
          <Text strong>Commande à rembourser :</Text><br />
          <Text>N° {order?.order_number || order?.id}</Text><br />
          <Text>Client : {order?.customer?.name || 'N/A'}</Text><br />
          <Text>Montant total : {Number(order?.total_amount || 0).toFixed(2)} {getDeviseActive()}</Text><br />
          <Text>Statut : </Text>
          <Tag color={getOrderStatusColor(order?.status)}>
            {getStatusLabel(order?.status)}
          </Tag>
        </div>

        <Form
          form={refundForm}
          layout="vertical"
          onFinish={handleRefundOrder}
        >
          <Form.Item
            name="amount"
            label="Montant du remboursement"
            rules={[
              { required: true, message: 'Veuillez saisir le montant' },
              { type: 'number', min: 0.01, message: 'Le montant doit être supérieur à 0' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={`Montant à rembourser en ${getDeviseActive()}`}
              precision={2}
              step={100}
              addonAfter={getDeviseActive()}
              max={order?.total_amount || 0}
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Motif du remboursement"
            rules={[{ required: true, message: 'Veuillez saisir le motif du remboursement' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Expliquez la raison du remboursement..."
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                onClick={() => setIsRefundModalVisible(false)}
                icon={<CloseOutlined />}
              >
                Annuler
              </Button>
              <Button 
                type="primary" 
                danger
                htmlType="submit"
                loading={saving}
                icon={<DollarOutlined />}
              >
                Effectuer le remboursement
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de report de commande */}
      <Modal
        title="Reporter la livraison"
        open={isRescheduleModalVisible}
        onCancel={() => setIsRescheduleModalVisible(false)}
        footer={null}
        width={window.innerWidth < 768 ? '95vw' : 500}
        style={window.innerWidth < 768 ? { top: 20 } : {}}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Modification de la date de livraison</Text>
          <Text style={{ display: 'block', marginTop: 8 }}>
            Vous pouvez changer la date de livraison prévue pour cette commande.
          </Text>
        </div>

        <div style={{ marginBottom: 16, padding: '12px', backgroundColor: 'var(--private-neutral-surface, #f6f6f6)', borderRadius: '6px' }}>
          <Text strong>Commande concernée :</Text><br />
          <Text>N° {order?.order_number || order?.id}</Text><br />
          <Text>Client : {order?.customer?.name || 'N/A'}</Text><br />
          <Text>Montant : {Number(order?.total_amount || 0).toFixed(2)} {getDeviseActive()}</Text><br />
          <Text>Date actuelle : </Text>
          {order?.delivery_date ? (
            /* Parse la date sans conversion de fuseau horaire pour afficher la date exacte */
            <Text strong>{dayjs.utc(order.delivery_date).format('DD/MM/YYYY')}</Text>
          ) : (
            <Text type="secondary" italic>Non définie</Text>
          )}
        </div>

        <Form
          form={rescheduleForm}
          layout="vertical"
          onFinish={handleRescheduleOrder}
        >
          <Form.Item
            name="delivery_date"
            label="Nouvelle date de livraison"
            rules={[{ required: true, message: 'Veuillez sélectionner une nouvelle date de livraison' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder="Sélectionner une nouvelle date"
              defaultPickerValue={dayjs()}
              disabledDate={(current) => {
                if (!current) return true;

                // Convertir en dayjs pour une manipulation cohérente
                const currentDay = dayjs(current);
                const today = dayjs().startOf('day');
                const maxDate = dayjs().add(7, 'days').endOf('day');

                // Vérifier si la date est dans la plage autorisée (aujourd'hui à +7 jours)
                return !currentDay.isBetween(today, maxDate, 'day', '[]');
              }}
              showToday={true}
              allowClear={true}
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Motif du report (optionnel)"
          >
            <Input.TextArea
              rows={3}
              placeholder="Expliquez la raison du report de la livraison..."
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => setIsRescheduleModalVisible(false)}
                icon={<CloseOutlined />}
              >
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={saving}
                icon={<CalendarOutlined />}
              >
                Reporter la livraison
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal d'édition des items de la commande */}
      <Modal
        title="Modifier les produits de la commande"
        open={isEditItemsModalVisible}
        onCancel={() => setIsEditItemsModalVisible(false)}
        footer={null}
        width={window.innerWidth < 768 ? '95vw' : 900}
        style={window.innerWidth < 768 ? { top: 20 } : {}}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Gestion des articles</Text>
          <Text style={{ display: 'block', marginTop: 8, color: '#666' }}>
            Vous pouvez ajouter, modifier ou supprimer des produits de cette commande.
            Le montant total sera automatiquement recalculé.
          </Text>
        </div>

        {/* Section pour ajouter un nouveau produit */}
        <div style={{ marginBottom: 24, padding: '16px', backgroundColor: 'var(--private-neutral-surface, #f6f6f6)', borderRadius: '6px' }}>
          <Text strong style={{ display: 'block', marginBottom: 12 }}>Ajouter un produit</Text>
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="Rechercher et sélectionner un produit..."
            loading={loadingProducts}
            onSearch={(value) => {
              // Met à jour seulement le texte de recherche
              // Le useEffect avec debounce se chargera d'appeler loadAvailableProducts après 500ms
              setSearchProductText(value);
            }}
            onChange={handleAddProduct}
            filterOption={false}
            notFoundContent={loadingProducts ? <Spin size="small" /> : 'Aucun produit trouvé'}
            value={null}
          >
            {availableProducts.map(product => (
              product.product_variants?.map(variant => {
                const stock = variant.inventory_quantity || 0;
                const isOutOfStock = stock < 1;
                return (
                  <Select.Option
                    key={variant.id}
                    value={variant.id}
                    disabled={isOutOfStock}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {variant.image_url || product.main_image ? (
                        <img
                          src={variant.image_url || product.main_image}
                          alt={product.name}
                          style={{ width: 30, height: 30, objectFit: 'cover', borderRadius: '4px' }}
                        />
                      ) : null}
                      <span style={{ opacity: isOutOfStock ? 0.5 : 1 }}>
                        {product.name} - {variant.title}
                      </span>
                      <Text type="secondary" style={{ marginLeft: 'auto' }}>
                        {Number(variant.price || 0).toFixed(0)} {getDeviseActive()}
                      </Text>
                      <Tag color={isOutOfStock ? 'red' : stock < 5 ? 'orange' : 'green'} style={{ marginLeft: '8px' }}>
                        Stock: {stock}
                      </Tag>
                    </div>
                  </Select.Option>
                );
              })
            ))}
          </Select>
        </div>

        {/* Liste des produits actuels */}
        <div style={{ marginBottom: 24 }}>
          <Text strong style={{ display: 'block', marginBottom: 12 }}>
            Produits dans la commande ({editableItems.length})
          </Text>

          {editableItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px', backgroundColor: 'var(--private-neutral-surface, #fafafa)', borderRadius: '6px' }}>
              <Text type="secondary">Aucun produit dans la commande</Text>
            </div>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {editableItems.map((item, index) => (
                <Card
                  key={index}
                  size="small"
                  style={{ marginBottom: 12 }}
                  bodyStyle={{ padding: '12px' }}
                >
                  <Row gutter={[12, 12]} align="middle">
                    <Col xs={24} sm={8}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {item.image_url && (
                          <img
                            src={item.image_url}
                            alt={item.product_name}
                            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: '4px' }}
                          />
                        )}
                        <div>
                          <Text strong style={{ fontSize: '13px', display: 'block' }}>{item.product_name}</Text>
                          {/* Variation de l'article (taille, couleur, etc.) */}
                          {getVariantLabel(item.product_name, item.variant_title) && (
                            <Tag color="geekblue" style={{ marginTop: 4 }} className="private-tag-accent" >
                              {getVariantLabel(item.product_name, item.variant_title)}
                            </Tag>
                          )}
                        </div>
                      </div>
                    </Col>

                    <Col xs={12} sm={5}>
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                          Quantité
                          {item.available_stock !== undefined && (
                            <Text type="secondary" style={{ fontSize: '10px', marginLeft: '4px' }}>
                              (Max: {item.available_stock})
                            </Text>
                          )}
                        </Text>
                        <InputNumber
                          min={1}
                          max={item.available_stock || 999999}
                          value={item.quantity}
                          onChange={(value) => handleQuantityChange(index, value)}
                          style={{ width: '100%' }}
                        />
                        {item.available_stock !== undefined && item.available_stock < 10 && (
                          <Text type="warning" style={{ fontSize: '10px', display: 'block', marginTop: '2px' }}>
                            Stock bas ({item.available_stock} restants)
                          </Text>
                        )}
                      </div>
                    </Col>

                    <Col xs={12} sm={6}>
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                          Prix unitaire ({getDeviseActive()})
                        </Text>
                        <InputNumber
                          min={0}
                          value={item.product_price}
                          onChange={(value) => handlePriceChange(index, value)}
                          style={{ width: '100%' }}
                          formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                          parser={value => value.replace(/\s?/g, '')}
                        />
                      </div>
                    </Col>

                    <Col xs={12} sm={4}>
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                          Total
                        </Text>
                        <Text strong style={{ color: PRIMARY_COLOR }}>
                          {(item.quantity * item.product_price).toFixed(0)} {getDeviseActive()}
                        </Text>
                      </div>
                    </Col>

                    <Col xs={12} sm={1}>
                      <Button
                        type="text"
                        danger
                        icon={<CloseOutlined />}
                        onClick={() => handleRemoveItem(index)}
                        title="Supprimer ce produit"
                      />
                    </Col>
                  </Row>
                </Card>
              ))}
            </div>
          )}

          {/* Total général */}
          {editableItems.length > 0 && (
            <div style={{
              marginTop: 16,
              padding: '12px',
              backgroundColor: 'var(--private-soft, #e6f7ff)',
              borderRadius: '6px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Text strong style={{ fontSize: '16px' }}>Nouveau montant total :</Text>
              <Text strong style={{ fontSize: '18px', color: PRIMARY_COLOR }}>
                {editableItems.reduce((sum, item) => sum + (item.quantity * item.product_price), 0).toFixed(0)} {getDeviseActive()}
              </Text>
            </div>
          )}
        </div>

        {/* Boutons d'action */}
        <div style={{ textAlign: 'right', borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
          <Space>
            <Button
              onClick={() => setIsEditItemsModalVisible(false)}
              icon={<CloseOutlined />}
            >
              Annuler
            </Button>
            <Button
              type="primary"
              onClick={handleSaveOrderItems}
              loading={saving}
              icon={<SaveOutlined />}
              disabled={editableItems.length === 0}
            >
              Enregistrer les modifications
            </Button>
          </Space>
        </div>
      </Modal>

      {/* Modal d'envoi de message WhatsApp au client avec messages prédéfinis */}
      <SendWhatsAppMessageModal
        visible={isWhatsAppModalVisible}
        onCancel={() => setIsWhatsAppModalVisible(false)}
        phone={order.customer?.phone}
        destinataireNom={order.customer?.name}
        templates={TEMPLATES_MESSAGES_COMMANDE}
        // Variables issues de la commande (date de livraison formatée sans décalage horaire)
        variables={construireVariablesCommande(order, (date) => dayjs.utc(date).format('DD/MM/YYYY'))}
        variablesDisponibles={VARIABLES_COMMANDE}
        title={`Message au client — Commande ${order.order_number || ''}`}
      />
    </div>
  );
};

export default OrderDetails;
